import { useCallback, useEffect, useState } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PickingApiAdapter } from '../adapters/PickingApiAdapter';
import { Location } from '../models';
import type { PreparationLineWithProduct, PreparationWithProducts } from './usePreparationQuery';

export type PickingStep = 'cart' | 'location' | 'product';

export interface PickingState {
  currentStep: PickingStep;
  assignedCart?: string;
  selectedLocation?: Location;
  selectedLine?: PreparationLineWithProduct;
  scannedCode: string;
  availableLocations: Location[];
}

export const usePickingFlow = (
  preparation: PreparationWithProducts | null,
  updateLineQuantity?: (lineId: number, quantityToAdd: number) => void,
  updatePreparation?: (updatedPreparation: PreparationWithProducts) => void,
) => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new PickingApiAdapter(httpClient);

  const [pickingState, setPickingState] = useState<PickingState>({
    currentStep: preparation?.location_id ? 'location' : 'cart',
    assignedCart: preparation?.location_id ? `LOADING-${preparation.location_id}` : undefined,
    scannedCode: '',
    availableLocations: [],
  });

  // Actualizar estado cuando cambie la preparation y tenga location_id
  useEffect(() => {
    if (preparation?.location_id && !pickingState.assignedCart) {
      setPickingState((prev) => ({
        ...prev,
        currentStep: 'location',
        assignedCart: `LOADING-${preparation.location_id}`,
      }));
    }
  }, [preparation?.id, preparation?.location_id, pickingState.assignedCart]);

  // Cargar código del carrito si ya hay un location_id asignado
  useEffect(() => {
    const loadAssignedCart = async () => {
      if (preparation?.location_id && pickingState.assignedCart?.startsWith('LOADING-')) {
        try {
          const result = await adapter.getCart(preparation.location_id);

          if (result.success && result.code) {
            setPickingState((prev) => ({
              ...prev,
              assignedCart: result.code,
            }));
          } else {
            console.error('❌ usePickingFlow - Error al cargar carrito:', result.message);
            // Si no se puede cargar el carrito, volver al paso de carrito
            setPickingState((prev) => ({
              ...prev,
              currentStep: 'cart',
              assignedCart: undefined,
            }));
          }
        } catch (error) {
          console.error('❌ usePickingFlow - Error de conexión al cargar carrito:', error);
          // Si hay error de conexión, volver al paso de carrito
          setPickingState((prev) => ({
            ...prev,
            currentStep: 'cart',
            assignedCart: undefined,
          }));
        }
      }
    };

    loadAssignedCart();
  }, [preparation?.location_id, pickingState.assignedCart]);

  const getCurrentStep = (): PickingStep => {
    if (!pickingState.assignedCart || pickingState.assignedCart.startsWith('LOADING-')) return 'cart';
    if (!pickingState.selectedLocation) return 'location';
    return 'product';
  };

  // Procesar código escaneado
  const processScannedCode = useCallback(
    async (code: string) => {
      const currentStep = getCurrentStep();

      setPickingState((prev) => ({
        ...prev,
        scannedCode: code,
      }));

      switch (currentStep) {
        case 'cart':
          // Validar que sea un código de carrito (debe empezar con C)
          if (code.startsWith('C')) {
            if (!preparation?.id) {
              console.error('❌ usePickingFlow - No hay preparation.id');
              return { success: false, message: 'Error: No se pudo obtener el ID de la preparación' };
            }

            try {
              // Llamar al backend para asignar el carrito
              const result = await adapter.assignCart(preparation.id, code);

              if (result.success) {
                if (result.preparation && updatePreparation) {
                  updatePreparation(result.preparation);
                }
                setPickingState((prev) => ({
                  ...prev,
                  assignedCart: code,
                  currentStep: 'location',
                  scannedCode: '',
                }));
                return { success: true, message: result.message };
              } else {
                return { success: false, message: result.message };
              }
            } catch (error) {
              console.error('❌ usePickingFlow - Error en llamada al backend:', error);
              return { success: false, message: 'Error de conexión con el servidor' };
            }
          } else {
            return { success: false, message: `No se encontró ningún carrito con el código "${code}"` };
          }

        case 'location': {
          // Buscar la ubicación en todas las líneas
          const allLocations: Location[] = [];
          preparation?.lines.forEach((line) => {
            const suggestedLocations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
            const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
            allLocations.push(...suggestedLocations, ...otherLocations);
          });

          const trimmedCode = code.trim();
          const foundLocation = allLocations.find((loc) => loc.code?.trim() === trimmedCode);
          if (foundLocation) {
            // Encontrar las líneas que tienen productos en esta ubicación
            const linesInLocation =
              preparation?.lines.filter((line) => {
                const suggestedLocations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
                const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
                return [...suggestedLocations, ...otherLocations].some((loc) => loc.code?.trim() === trimmedCode);
              }) || [];

            // Verificar si todos los productos en esta ubicación están completados
            const pendingLinesInLocation = linesInLocation.filter((line) => line.qty_done < line.qty);

            if (pendingLinesInLocation.length === 0) {
              return {
                success: false,
                message: `Todos los productos en la ubicación ${code} ya han sido completados`,
              };
            }

            setPickingState((prev) => ({
              ...prev,
              selectedLocation: foundLocation,
              currentStep: 'product',
              availableLocations: linesInLocation.flatMap((line) => {
                const suggestedLocations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
                const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
                return [...suggestedLocations, ...otherLocations].filter((loc) => loc.code?.trim() === trimmedCode);
              }),
              scannedCode: '',
            }));
            return {
              success: true,
              message: `Ubicación ${code} seleccionada. ${pendingLinesInLocation.length} producto(s) pendiente(s)`,
            };
          } else {
            return { success: false, message: 'Ubicación no encontrada en esta preparación' };
          }
        }

        case 'product': {
          // Buscar el producto por código en la ubicación seleccionada
          const linesInCurrentLocation =
            preparation?.lines.filter((line) => {
              const suggestedLocations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
              const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
              return [...suggestedLocations, ...otherLocations].some(
                (loc) => loc.code?.trim() === pickingState.selectedLocation?.code?.trim(),
              );
            }) || [];

          const foundLine = linesInCurrentLocation.find((line) => {
            const trimmedCode = code.trim();
            const productCode = line.product?.code?.trim() || '';
            const productId = line.product_id.toString().trim();
            return productCode === trimmedCode || productId === trimmedCode;
          });

          if (foundLine) {
            setPickingState((prev) => ({
              ...prev,
              selectedLine: foundLine,
              scannedCode: '',
            }));
            return {
              success: true,
              message: `Producto ${foundLine.product?.name || foundLine.product_id} seleccionado`,
            };
          } else {
            return { success: false, message: 'Producto no encontrado en esta ubicación' };
          }
        }

        default:
          return { success: false, message: 'Estado inválido' };
      }
    },
    [pickingState.assignedCart, pickingState.selectedLocation, preparation, updatePreparation],
  );

  // Confirmar picking del producto
  const confirmPicking = useCallback(
    async (quantity: number, batchId?: number) => {
      if (!pickingState.selectedLine || !pickingState.selectedLocation) {
        return { success: false, message: 'No hay producto seleccionado' };
      }

      if (!preparation?.id) {
        return { success: false, message: 'Error: No se pudo obtener el ID de la preparación' };
      }

      const maxQuantity = pickingState.selectedLine.qty - pickingState.selectedLine.qty_done;
      const actualQuantity = Math.min(quantity, maxQuantity);

      if (actualQuantity <= 0) {
        return { success: false, message: 'Cantidad inválida' };
      }

      try {
        // Llamar al backend para confirmar el picking
        const result = await adapter.confirmPicking(
          preparation.id,
          pickingState.selectedLine.product_id,
          actualQuantity,
          pickingState.selectedLocation.id,
          batchId,
        );

        if (result.success) {
          // Si el backend devolvió la preparación actualizada, usarla
          if (result.preparation && updatePreparation) {
            updatePreparation(result.preparation);
          } else if (updateLineQuantity && pickingState.selectedLine) {
            // Fallback: actualización local optimista si no hay datos del backend
            updateLineQuantity(pickingState.selectedLine.id, actualQuantity);
          }

          // Resetear estado para siguiente producto
          setPickingState((prev) => ({
            ...prev,
            selectedLocation: undefined,
            selectedLine: undefined,
            currentStep: 'location',
            availableLocations: [],
          }));

          return {
            success: true,
            message: result.message,
            quantity: actualQuantity,
          };
        } else {
          return {
            success: false,
            message: result.message,
          };
        }
      } catch (error) {
        console.error('❌ usePickingFlow - Error en confirmPicking:', error);
        return {
          success: false,
          message: 'Error de conexión al confirmar el picking',
        };
      }
    },
    [pickingState.selectedLine, pickingState.selectedLocation, preparation, updateLineQuantity, updatePreparation],
  );

  const resetFlow = () => {
    setPickingState({
      currentStep: preparation?.location_id ? 'location' : 'cart',
      assignedCart: preparation?.location_id ? `CART-${preparation.location_id}` : undefined,
      scannedCode: '',
      availableLocations: [],
    });
  };

  const resetToLocationStep = () => {
    setPickingState((prev) => ({
      ...prev,
      selectedLocation: undefined,
      selectedLine: undefined,
      scannedCode: '',
      availableLocations: [],
    }));
  };

  const skipToNextStep = () => {
    const currentStep = getCurrentStep();
    switch (currentStep) {
      case 'cart':
        setPickingState((prev) => ({
          ...prev,
          assignedCart: 'CART-TEST-001',
          currentStep: 'location',
        }));
        break;
      case 'location': {
        const firstLine = preparation?.lines[0];
        const firstLocation =
          (Array.isArray(firstLine?.suggested_locations) && firstLine.suggested_locations[0]) || null;
        if (firstLocation) {
          setPickingState((prev) => ({
            ...prev,
            selectedLocation: firstLocation,
            currentStep: 'product',
          }));
        }
        break;
      }
    }
  };

  return {
    pickingState: {
      ...pickingState,
      currentStep: getCurrentStep(),
    },
    processScannedCode,
    confirmPicking,
    resetFlow,
    resetToLocationStep,
    skipToNextStep,
    // Helpers
    getStepDescription: () => {
      // Si el carrito se está cargando, mostrar mensaje de carga
      if (pickingState.assignedCart?.startsWith('LOADING-')) {
        return 'Cargando carrito asignado...';
      }

      const step = getCurrentStep();
      switch (step) {
        case 'cart':
          return 'Escanea el código del carrito';
        case 'location':
          return 'Escanea el código de la ubicación';
        case 'product':
          return `Escanea el producto en la ubicación ${pickingState.selectedLocation?.code}`;
        default:
          return 'Estado desconocido';
      }
    },
    getAvailableProductsInLocation: () => {
      if (!pickingState.selectedLocation) return [];
      return (
        preparation?.lines.filter((line) => {
          // Solo productos pendientes (no completados)
          if (line.qty_done >= line.qty) return false;

          const suggestedLocations = Array.isArray(line.suggested_locations) ? line.suggested_locations : [];
          const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
          return [...suggestedLocations, ...otherLocations].some(
            (loc) => loc.code?.trim() === pickingState.selectedLocation?.code?.trim(),
          );
        }) || []
      );
    },
  };
};
